import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { Text, Box, useMatcapTexture, Octahedron } from '@react-three/drei'

// ─── ThinFilmFresnelMap (inline) ────────────────────────────────────────────
class ThinFilmFresnelMap extends THREE.DataTexture {
  constructor(thickness = 500, refractiveIndexFilm = 1.4, refractiveIndexBase = 2.5, size = 512) {
    const data = new Uint8Array(size * 4)
    for (let i = 0; i < size; i++) {
      const cosTheta = i / size
      const cos1 = cosTheta
      const sin1 = Math.sqrt(1 - cos1 * cos1)
      const sin2Film = sin1 / refractiveIndexFilm
      const cos2Film = Math.sqrt(Math.max(0, 1 - sin2Film * sin2Film))
      const sin2Base = sin1 / refractiveIndexBase
      const cos2Base = Math.sqrt(Math.max(0, 1 - sin2Base * sin2Base))

      const rFilmS = (cos1 - refractiveIndexFilm * cos2Film) / (cos1 + refractiveIndexFilm * cos2Film)
      const rFilmP = (refractiveIndexFilm * cos1 - cos2Film) / (refractiveIndexFilm * cos1 + cos2Film)
      const rBaseS = (refractiveIndexFilm * cos2Film - refractiveIndexBase * cos2Base) / (refractiveIndexFilm * cos2Film + refractiveIndexBase * cos2Base)
      const rBaseP = (refractiveIndexBase * cos2Film - refractiveIndexFilm * cos2Base) / (refractiveIndexBase * cos2Film + refractiveIndexFilm * cos2Base)

      const delta = (4 * Math.PI * refractiveIndexFilm * cos2Film * thickness) / 550

      let r = 0
      for (let wl = 380; wl <= 780; wl += 10) {
        const deltaWl = (4 * Math.PI * refractiveIndexFilm * cos2Film * thickness) / wl
        const RS = (rFilmS * rFilmS + rBaseS * rBaseS + 2 * rFilmS * rBaseS * Math.cos(2 * deltaWl)) /
          (1 + rFilmS * rFilmS * rBaseS * rBaseS + 2 * rFilmS * rBaseS * Math.cos(2 * deltaWl))
        const RP = (rFilmP * rFilmP + rBaseP * rBaseP + 2 * rFilmP * rBaseP * Math.cos(2 * deltaWl)) /
          (1 + rFilmP * rFilmP * rBaseP * rBaseP + 2 * rFilmP * rBaseP * Math.cos(2 * deltaWl))
        r += (RS + RP) * 0.5
      }
      r /= 41

      const idx = i * 4
      const rgb = iridescenceToRGB(r, i / size)
      data[idx] = rgb[0]
      data[idx + 1] = rgb[1]
      data[idx + 2] = rgb[2]
      data[idx + 3] = 255
    }
    super(data, size, 1, THREE.RGBAFormat)
    this.needsUpdate = true
    this.wrapS = this.wrapT = THREE.RepeatWrapping
  }
}

function iridescenceToRGB(r, t) {
  const hue = (t * 360 + r * 180) % 360
  const s = 0.9, l = 0.4 + r * 0.3
  const h = hue / 360
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const toRGB = (t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1
    if (t < 1/6) return p + (q - p) * 6 * t
    if (t < 1/2) return q
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
    return p
  }
  return [
    Math.round(toRGB(h + 1/3) * 255),
    Math.round(toRGB(h) * 255),
    Math.round(toRGB(h - 1/3) * 255),
  ]
}

// ─── Mirror data ─────────────────────────────────────────────────────────────
const MIRRORS = [
  { position: [-6, 0, 0], args: [0.5, 2, 0.1], rotation: [0, 0.3, 0] },
  { position: [-3, 1, -1], args: [0.5, 2.5, 0.1], rotation: [0, -0.2, 0.1] },
  { position: [0, -0.5, 0.5], args: [0.5, 2, 0.1], rotation: [0, 0.5, -0.1] },
  { position: [3, 0.5, -0.5], args: [0.5, 2.5, 0.1], rotation: [0, -0.4, 0] },
  { position: [6, 0, 0], args: [0.5, 2, 0.1], rotation: [0, 0.2, 0.1] },
  { position: [-5, -1.5, 1], args: [0.5, 1.5, 0.1], rotation: [0.1, 0.6, 0] },
  { position: [5, 1.5, 1], args: [0.5, 1.5, 0.1], rotation: [-0.1, -0.6, 0] },
  { position: [0, 2, -1], args: [0.5, 1.5, 0.1], rotation: [0, 0, 0.2] },
]

// ─── useSlerp (mouse follow) ─────────────────────────────────────────────────
function useSlerp() {
  const ref = useRef()
  const mouse = useRef({ x: 0, y: 0 })
  const target = useRef(new THREE.Quaternion())

  useEffect(() => {
    const onMove = (e) => {
      mouse.current.x = (e.clientX / window.innerWidth - 0.5) * 0.3
      mouse.current.y = (e.clientY / window.innerHeight - 0.5) * 0.3
    }
    window.addEventListener('mousemove', onMove)
    return () => window.removeEventListener('mousemove', onMove)
  }, [])

  useFrame(() => {
    if (!ref.current) return
    const euler = new THREE.Euler(-mouse.current.y, mouse.current.x, 0)
    target.current.setFromEuler(euler)
    ref.current.quaternion.slerp(target.current, 0.05)
  })

  return ref
}

// ─── RenderTarget ─────────────────────────────────────────────────────────────
function useRenderTarget() {
  const renderTarget = useMemo(() => new THREE.WebGLCubeRenderTarget(256, {
    format: THREE.RGBAFormat,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
  }), [])
  const cubeCamera = useMemo(() => new THREE.CubeCamera(0.1, 1000, renderTarget), [renderTarget])
  const { gl, scene } = useThree()

  useFrame(() => {
    cubeCamera.update(gl, scene)
  })

  return [cubeCamera, renderTarget]
}

// ─── Components ───────────────────────────────────────────────────────────────
function Mirror({ sideMat, reflectMat, args, position, rotation }) {
  const ref = useRef()
  useFrame(() => {
    if (ref.current) {
      ref.current.rotation.y += 0.001
      ref.current.rotation.z += 0.01
    }
  })
  return (
    <Box
      ref={ref}
      args={args}
      position={position}
      rotation={rotation}
      material={[sideMat, sideMat, sideMat, sideMat, reflectMat, reflectMat]}
    />
  )
}

function Mirrors({ envMap }) {
  const [thinFilm] = useState(() => new ThinFilmFresnelMap())
  const sideMat = useMemo(() => new THREE.MeshLambertMaterial({ map: thinFilm, color: '#AAAAAA' }), [thinFilm])
  const reflectMat = useMemo(() => new THREE.MeshLambertMaterial({ map: thinFilm, envMap, color: '#FFFFFF' }), [thinFilm, envMap])

  return (
    <group>
      {MIRRORS.map((m, i) => (
        <Mirror key={i} sideMat={sideMat} reflectMat={reflectMat} {...m} />
      ))}
    </group>
  )
}

function Title({ position }) {
  const ref = useRef()
  useEffect(() => { if (ref.current) ref.current.lookAt(0, 0, 0) }, [])
  return (
    <group ref={ref} position={position}>
      <Text
        depthTest={false}
        material-toneMapped={false}
        material-color="#FFFFFF"
        textAlign="center"
        fontSize={2.5}
        maxWidth={10}
        font="https://fonts.gstatic.com/s/syncopate/v12/pe0pMIuPIYBCpEV5eFdKvtKqBP5p.woff"
      >
        {`Portfolio\nQUENTIN OTT`}
      </Text>
    </group>
  )
}

function TitleCopies() {
  const positions = useMemo(() => {
    const geo = new THREE.IcosahedronGeometry(10)
    const pos = geo.getAttribute('position')
    const pts = []
    for (let i = 0; i < pos.count; i++) {
      pts.push([pos.getX(i), pos.getY(i), pos.getZ(i)])
    }
    geo.dispose()
    return pts
  }, [])

  return (
    <group>
      {positions.map((p, i) => (
        <Title key={i} position={p} />
      ))}
    </group>
  )
}

// ─── Scene ────────────────────────────────────────────────────────────────────
export default function Scene1() {
  const [, renderTarget] = useRenderTarget()
  const group = useSlerp()
  const [matcap] = useMatcapTexture('C8D1DC_575B62_818892_6E747B', 512)

  return (
    <>
      <group ref={group}>
        <Octahedron args={[20, 4, 4]} position={[0, 0, -5]}>
          <meshMatcapMaterial matcap={matcap} side={THREE.BackSide} transparent opacity={0.3} color="#FFFFFF" />
        </Octahedron>
        <Title position={[0, 0, -10]} />
        <TitleCopies />
        <Mirrors envMap={renderTarget.texture} />
      </group>
      <ambientLight intensity={0.5} />
      <pointLight position={[10, 10, 10]} intensity={1} />
    </>
  )
}
